import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { RenderRequest, RenderResponse } from './response-render.types';
import { renderResponse } from './response-render.core';

@Injectable({ providedIn: 'root' })
export class ResponseRenderService {
  private worker?: Worker;
  private pending = new Map<string, (v: RenderResponse) => void>();
  private forceInThread = false;

  constructor() {
    this.tryInitWorker();
  }

  private tryInitWorker(): void {
    if (typeof Worker === 'undefined') return;

    try {
      this.worker = new Worker(new URL('./response-render.worker', import.meta.url), { type: 'module' });
      this.worker.onmessage = ({ data }: MessageEvent<RenderResponse>) => {
        const cb = this.pending.get(data.id);
        if (cb) {
          this.pending.delete(data.id);
          cb(data);
        }
      };
      this.worker.onerror = () => {
        // If it fails at runtime, fall back.
        this.forceInThread = true;
        this.worker?.terminate();
        this.worker = undefined;
      };
    } catch {
      // This catches the SecurityError you see in production.
      this.forceInThread = true;
      this.worker = undefined;
    }
  }

  render(req: Omit<RenderRequest, 'id'>): Observable<RenderResponse> {
    return new Observable<RenderResponse>(sub => {
      const id = crypto.randomUUID();
      const fullReq = { ...req, id } satisfies RenderRequest;

      // Fallback path (no worker, or worker blocked by browser)
      if (this.forceInThread || !this.worker) {
        try {
          sub.next(renderResponse(fullReq));
          sub.complete();
        } catch (e) {
          sub.error(e);
        }
        return;
      }

      // Worker path
      this.pending.set(id, res => {
        sub.next(res);
        sub.complete();
      });

      try {
        this.worker.postMessage(fullReq);
      } catch (e) {
        // If posting fails, fall back immediately for this call
        this.pending.delete(id);
        this.forceInThread = true;
        this.worker?.terminate();
        this.worker = undefined;

        try {
          sub.next(renderResponse(fullReq));
          sub.complete();
        } catch (err) {
          sub.error(err);
        }
      }

      return () => this.pending.delete(id);
    });
  }
}
